import { DANGER, GREEN } from '../Theme/palette';
import { useView } from '@hooks';
import { Pagination, Tooltip } from 'antd';
import { DownOutlined, InfoCircleFilled, UpOutlined } from '@ant-design/icons';
import React, { useState } from 'react';

export interface ISummaryStat {
  label: string;
  value: string;
  /** Colours the figure: green for money made, red for money lost. */
  tone?: 'positive' | 'negative';
  /** The one figure a phone shows while the bar is collapsed. */
  primary?: boolean;
}

export interface ISummaryPagination {
  current?: number;
  pageSize: number;
  total: number;
  onChange: (page: number, pageSize: number) => void;
}

interface IProps {
  /** The headline on the left, e.g. "24 filtered jobs". Omit to show the pager alone. */
  title?: string;
  /** Shown on hover of the info icon beside the title - says what the figures cover. */
  info?: string;
  stats?: ISummaryStat[];
  loading?: boolean;
  /** The table's pager, kept here so it stays in view beside the totals. Desktop only. */
  pagination?: ISummaryPagination;
}

/** Green for money made, red for money lost; zero reads as made. */
export const profitTone = (value: number): ISummaryStat['tone'] =>
  value < 0 ? 'negative' : 'positive';

const TONE: Record<NonNullable<ISummaryStat['tone']>, string> = {
  positive: GREEN.tag,
  negative: DANGER.active,
};

/** The totals strip under a list: sticks to the bottom of the viewport so it is in view however
 * long the table is, and reads across the whole filtered set rather than the page. */
export const SummaryBar: React.FC<IProps> = ({ title, info, stats = [], loading, pagination }) => {
  const { isMobile } = useView();
  const [expanded, setExpanded] = useState(false);

  // A phone has no room for a row of eight figures: one line with the headline figure, and the rest
  // a tap away.
  if (isMobile) {
    const primary = stats.find((s) => s.primary);
    if (!title && !stats.length) return null;
    return (
      <div
        style={{
          position: 'sticky',
          bottom: 0,
          zIndex: 5,
          marginTop: 8,
          background: '#fff',
          borderTop: '1px solid #e8e8e8',
          boxShadow: '0 -2px 6px rgba(0,0,0,0.04)',
          opacity: loading ? 0.6 : 1,
        }}
      >
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          style={{
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            padding: '8px 12px',
            border: 0,
            background: 'transparent',
            font: 'inherit',
            cursor: stats.length ? 'pointer' : 'default',
          }}
        >
          <span style={{ fontWeight: 700, fontSize: 13 }}>{title}</span>
          {primary && (
            <span style={{ fontSize: 13 }}>
              <span style={{ color: '#8c8c8c', marginRight: 6 }}>{primary.label}</span>
              <strong style={{ color: primary.tone ? TONE[primary.tone] : undefined }}>
                {primary.value}
              </strong>
            </span>
          )}
          {stats.length > 0 && (expanded ? <DownOutlined /> : <UpOutlined />)}
        </button>
        {expanded && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 8,
              padding: '4px 12px 12px',
            }}
          >
            {stats.map((stat) => (
              <div key={stat.label}>
                <div style={{ fontSize: 11, color: '#8c8c8c' }}>{stat.label}</div>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: stat.tone ? TONE[stat.tone] : 'rgba(0,0,0,0.85)',
                  }}
                >
                  {stat.value}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'sticky',
        bottom: 0,
        zIndex: 5,
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        flexWrap: 'wrap',
        padding: '12px 16px',
        marginTop: 12,
        background: '#fff',
        borderTop: '1px solid #e8e8e8',
        boxShadow: '0 -2px 6px rgba(0,0,0,0.04)',
        opacity: loading ? 0.6 : 1,
        transition: 'opacity 0.2s',
      }}
    >
      {title && (
        <div style={{ fontWeight: 700, fontSize: 14, whiteSpace: 'nowrap' }}>
          {title}{' '}
          {info && (
            <Tooltip title={info}>
              <InfoCircleFilled style={{ color: '#8c8c8c', fontSize: 13 }} />
            </Tooltip>
          )}
        </div>
      )}
      {stats.map((stat) => (
        <div
          key={stat.label}
          style={{ borderLeft: '1px solid #e8e8e8', paddingLeft: 16, textAlign: 'left' }}
        >
          <div style={{ fontSize: 11, color: '#8c8c8c' }}>{stat.label}</div>
          <div
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: stat.tone ? TONE[stat.tone] : 'rgba(0,0,0,0.85)',
            }}
          >
            {stat.value}
          </div>
        </div>
      ))}
      {pagination && (
        <Pagination
          style={{ marginLeft: 'auto', fontSize: 14 }}
          showSizeChanger
          pageSizeOptions={['10', '15', '20', '30', '50', '100']}
          current={pagination.current}
          pageSize={pagination.pageSize}
          total={pagination.total}
          showTotal={(total, range) => `${range[0]}-${range[1]} of ${total}`}
          onChange={pagination.onChange}
        />
      )}
    </div>
  );
};
